/* eslint-disable import/order */
// !!!NOTE: Disabling the importing order rule as we want the settings to be imported before any other imports. Maybe fix this in the future.

import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import dayjs from "dayjs";
import React, { Suspense, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { RouterProvider } from "react-router-dom";
import "dayjs/locale/fi";
import "dayjs/locale/sv";

// !! IMOPORTANT  !! These needs to be initialized before any @krea/customer-features import //
import "./i18n";
import TagManager from "react-gtm-module";

import HttpService from "@krea/customer-features/services/httpService";
import { StylingWrapper } from "@krea/customer-features/shared-components/StylingWrapper";
import { queryClient } from "@krea/customer-features/store/queryClient";
import { config } from "@krea/shared/config";
import { CONFIG_CATALOG } from "@krea/shared/config/catalog";
import {
  initSentryOptions,
  SENTRY_REPLAY_PRIVACY_DEFAULTS,
} from "@krea/shared/utils/initSentry";

import { router } from "./router";
import settings from "./settings";

dayjs.locale("sv");

const sentryOptions: Sentry.BrowserOptions = {
  ...initSentryOptions(config, { filterBrowserNoise: true }),
  dsn: "https://8e0b9c356265313a88a8cf99ef482641@o242905.ingest.us.sentry.io/4508332357255168",
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      ...SENTRY_REPLAY_PRIVACY_DEFAULTS,
      maskAllInputs: false,
      networkDetailAllowUrls: [`${CONFIG_CATALOG.production.apiRootUrl}/api/`],
    }),
  ],
};

// eslint-disable-next-line react-refresh/only-export-components
function AppWithCallbackAfterRender() {
  const isDeployedEnvironment = config.isSentryEnabled;

  useEffect(() => {
    const instance = HttpService.getInstance();
    instance.setupInterceptors();

    const tagManagerArgs = {
      gtmId: settings.gtmId,
      dataLayer: {
        event: "initTracking",
        environment: settings.environment,
      },
    };

    TagManager.initialize(tagManagerArgs);
  }, []);

  useEffect(() => {
    Sentry.init(sentryOptions);
  }, [isDeployedEnvironment]);

  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <Suspense fallback={null}>
            {!isDeployedEnvironment && (
              <ReactQueryDevtools initialIsOpen={false} />
            )}
            <StylingWrapper>
              <RouterProvider router={router} />
            </StylingWrapper>
          </Suspense>
        </HelmetProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
}

const container = document.getElementById("root");
const root = createRoot(container!);

root.render(<AppWithCallbackAfterRender />);
